import React, { useEffect, useRef, useState } from "react";
import { Send } from "lucide-react";
import Card from "../common/Card";
import Button from "../common/Button";
import Badge from "../common/Badge";
import { parseMoney, formatMoneyDisplay, formatMoneyInput } from "../../utils/money";
import { useToast } from "../../hooks/useToast";
import { supabase } from "../../supabaseClient";
import { generateInvoicePDF } from "../../utils/generateQuotePDF";

const GST_RATE = 0.1;

function InvoiceStatusBadge({ status }) {
  if (!status || status === "pending") {
    return <Badge type="info">Awaiting customer</Badge>;
  }
  if (status === "acknowledged") return <Badge type="success">Acknowledged</Badge>;
  if (status === "paid") return <Badge type="success">Paid</Badge>;
  return <Badge>{status}</Badge>;
}

/**
 * Tradie-side invoice composer. Pre-fills line items from the lead's
 * accepted quote so the tradie can send the invoice with one click,
 * or amend it (extra time, materials, etc.) and call out the changes
 * in the amendments_summary field.
 *
 * @param {Object} props
 * @param {import("../../types").SelectedLead} props.selected - lead with .quote on it
 * @param {string|null} [props.businessId]
 * @param {import("../../types").Profile|null} [props.profile]
 * @param {(token:string) => string} [props.getCustomerInvoiceLink]
 * @param {() => Promise<void>} [props.onSaved] - parent refresh hook
 */
export default function InvoiceBuilder({ selected, businessId, profile, getCustomerInvoiceLink, onSaved }) {
  const { showToast } = useToast();
  const idCounter = useRef(0);
  function nextId() { idCounter.current += 1; return idCounter.current; }

  const gstRegistered = profile?.gst_registered !== false;

  const [existingInvoice, setExistingInvoice] = useState(null);
  const [lineItems, setLineItems] = useState([{ _id: 1, description: "Labour", amount: 0 }]);
  const [introText, setIntroText] = useState("");
  const [amendmentsSummary, setAmendmentsSummary] = useState("");
  const [saving, setSaving] = useState(false);
  const [loadingExisting, setLoadingExisting] = useState(false);

  const leadId = selected?.id;
  const acceptedQuote = selected?.quote;

  // Load any existing invoice for this lead OR seed line items from the
  // accepted quote. We treat the most-recent invoice as canonical.
  useEffect(() => {
    if (!leadId) return;
    let cancelled = false;
    async function loadExisting() {
      setLoadingExisting(true);
      const { data: invoices } = await supabase
        .from("invoices")
        .select("*")
        .eq("lead_id", leadId)
        .order("created_at", { ascending: false })
        .limit(1);
      if (cancelled) return;

      if (invoices && invoices.length > 0) {
        const inv = invoices[0];
        setExistingInvoice(inv);
        setIntroText(inv.intro_text || "");
        setAmendmentsSummary(inv.amendments_summary || "");
        const items = Array.isArray(inv.line_items) ? inv.line_items : [];
        if (items.length > 0) {
          const base = Date.now();
          setLineItems(items.map((it, i) => ({
            _id: base + i,
            description: it.description || "",
            amount: parseMoney(it.amount),
          })));
        }
      } else if (acceptedQuote?.line_items && Array.isArray(acceptedQuote.line_items) && acceptedQuote.line_items.length > 0) {
        // Seed from the accepted quote — this is the common path.
        const base = Date.now();
        setLineItems(acceptedQuote.line_items.map((it, i) => ({
          _id: base + i,
          description: it.description || "",
          amount: parseMoney(it.amount),
        })));
        setIntroText(acceptedQuote.notes || acceptedQuote.intro_text || "");
        setExistingInvoice(null);
      } else {
        setLineItems([{ _id: nextId(), description: "Labour", amount: 0 }]);
        setExistingInvoice(null);
      }
      setLoadingExisting(false);
    }
    loadExisting();
    return () => { cancelled = true; };
  }, [leadId, acceptedQuote]);

  function addLineItem() {
    setLineItems((p) => [...p, { _id: nextId(), description: "", amount: 0 }]);
  }
  function updateLineItem(index, field, value) {
    setLineItems((prev) => prev.map((item, i) =>
      i !== index ? item : (field === "amount"
        ? { ...item, amount: parseMoney(value) }
        : { ...item, [field]: value })
    ));
  }
  function removeLineItem(index) {
    setLineItems((p) => p.filter((_, i) => i !== index));
  }

  const subtotal = lineItems.reduce((sum, it) => sum + (parseMoney(it.amount) || 0), 0);
  const gst = gstRegistered ? subtotal * GST_RATE : 0;
  const total = subtotal + gst;

  async function handleSaveAndSend() {
    if (!selected || !businessId) return;
    if (subtotal <= 0) {
      showToast("Add at least one line item with an amount before sending.", "error");
      return;
    }
    if (!lineItems.some((it) => (it.description || "").trim())) {
      showToast("Add a description to at least one line item before sending.", "error");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        business_id: businessId,
        lead_id: selected.id,
        quote_id: acceptedQuote?.id || null,
        line_items: lineItems.map((it) => ({
          description: it.description,
          amount: parseMoney(it.amount),
        })),
        subtotal,
        gst,
        total,
        gst_registered: gstRegistered,
        intro_text: introText,
        amendments_summary: amendmentsSummary,
        status: "sent",
        sent_at: new Date().toISOString(),
      };

      let savedRow;
      if (existingInvoice?.id) {
        const { data, error } = await supabase
          .from("invoices")
          .update(payload)
          .eq("id", existingInvoice.id)
          .select()
          .single();
        if (error) throw error;
        savedRow = data;
      } else {
        const { data, error } = await supabase
          .from("invoices")
          .insert(payload)
          .select()
          .single();
        if (error) throw error;
        savedRow = data;
      }

      // Stamp a human-readable invoice_number once we know the row id.
      if (savedRow && !savedRow.invoice_number) {
        const num = `INV-${String(savedRow.id).slice(0, 8).toUpperCase()}`;
        await supabase.from("invoices").update({ invoice_number: num }).eq("id", savedRow.id);
        savedRow.invoice_number = num;
      }
      setExistingInvoice(savedRow);

      // Auto-advance the lead's pipeline stage to "Invoiced" + stamp
      // invoiced_at / invoice_sent so the kanban shows it under the
      // amber Invoiced column. Don't downgrade if the lead is already
      // past Invoiced (Done = paid). Best-effort — non-fatal on error.
      try {
        const currentStage = String(selected?.status || "").toLowerCase();
        if (currentStage !== "done" && currentStage !== "declined") {
          await supabase
            .from("leads")
            .update({
              status: "Invoiced",
              invoice_sent: true,
              invoiced_at: new Date().toISOString(),
            })
            .eq("id", selected.id);
        }
      } catch {
        // Non-fatal — invoice email still fires below.
      }

      // Email the customer (best-effort -- never block the save).
      try {
        const customerName =
          selected.form?.fullName || selected.customer || "your customer";
        const customerEmail =
          selected.form?.email ||
          selected.email ||
          selected.customer_email ||
          selected.captured_info?.email ||
          selected.lead_details?.email ||
          "";
        const tradieBusinessName = profile?.business_name || profile?.full_name || "Your tradie";
        const invoiceUrl = savedRow?.public_token && getCustomerInvoiceLink
          ? getCustomerInvoiceLink(savedRow.public_token)
          : "";

        // Generate the invoice PDF (best-effort — non-fatal on failure).
        let pdfBase64 = null;
        try {
          const pdfProfile = {
            ...(profile || {}),
            full_name: profile?.full_name || "",
            business_name: tradieBusinessName,
          };
          const pdfResult = await generateInvoicePDF({
            lead: selected,
            lineItems,
            notes: introText,
            totals: { subtotal, gst, total },
            gstRegistered,
            profile: pdfProfile,
            docNumber: savedRow.invoice_number,
            invoiceStatus: savedRow.status === "paid" ? "paid" : "sent",
            payment: {
              accountName: profile?.bank_account_name || "",
              bsb: profile?.bank_bsb || "",
              accountNumber: profile?.bank_account_number || "",
              reference: savedRow.invoice_number,
            },
            acceptUrl: invoiceUrl,
          });
          pdfBase64 = pdfResult.base64;
        } catch {
          // Email still goes out without an attachment.
        }

        if (customerEmail) {
          const { data: { session: authSession } } = await supabase.auth.getSession();
          const tradieEmail = authSession?.user?.email || "";
          const functionUrl = `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/send-invoice-email`;
          const response = await fetch(functionUrl, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${authSession?.access_token || ""}`,
              apikey: process.env.REACT_APP_SUPABASE_ANON_KEY,
            },
            body: JSON.stringify({
              customerEmail,
              customerName,
              tradieName: profile?.full_name || "",
              tradieEmail,
              tradieBusinessName,
              tradiePhone: profile?.phone || "",
              tradieLogoUrl: profile?.logo_url || "",
              tradieAbn: profile?.abn || "",
              tradieLicence: profile?.licence_number || "",
              tradieInsuranceProvider:
                profile?.has_insurance ? profile?.insurance_provider || "" : "",
              tradieInsurancePolicy:
                profile?.has_insurance ? profile?.insurance_policy_number || "" : "",
              invoiceNumber: savedRow.invoice_number,
              totalAmount: total,
              originalQuoteTotal: Number(acceptedQuote?.total) || null,
              amendmentsSummary,
              introText,
              invoiceUrl,
              pdfBase64,
            }),
          });
          if (response.ok) {
            showToast(`Invoice sent to ${customerName}.`, "success");
          } else {
            const errText = await response.text().catch(() => "");
            showToast(`Invoice saved, but email failed: ${response.status} ${errText}`, "error");
          }
        } else {
          showToast(`Invoice saved, but no customer email is on file for ${customerName}.`, "error");
        }
      } catch (emailErr) {
        showToast(`Invoice saved, but email failed: ${emailErr.message || "unknown error"}`, "error");
      }

      await onSaved?.();
    } catch (err) {
      showToast(err.message || "Failed to save invoice.", "error");
    } finally {
      setSaving(false);
    }
  }

  if (loadingExisting) {
    return (
      <Card>
        <p className="muted">Loading invoice…</p>
      </Card>
    );
  }

  return (
    <Card>
      <div className="quote-edit-zone">
      <h3>
        <Send size={18} /> Invoice builder
      </h3>

      <p className="muted">
        {!acceptedQuote
          ? "No accepted quote on this lead yet — you can still build an invoice from scratch."
          : gstRegistered
          ? "Pre-filled from the accepted quote. Amend amounts or add lines for site changes — GST is calculated automatically."
          : "Pre-filled from the accepted quote. GST is not applied because you are not GST registered."}
      </p>

      {/* ── Existing-invoice status (mirrors quote status placement) ── */}
      {existingInvoice && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "12px",
            padding: "10px 14px",
            marginBottom: "14px",
            background: "var(--surface-3, var(--surface, #fafafa))",
            border: "1px solid var(--hairline, var(--border, rgba(140,100,80,0.14)))",
            borderRadius: "12px",
            flexWrap: "wrap",
          }}
        >
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: "0.72rem", textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 700, color: "var(--text-3, #6b5448)" }}>
              {existingInvoice.invoice_number || "Latest invoice"}
            </div>
            <div style={{ fontSize: "1.05rem", fontWeight: 700, marginTop: "2px", fontVariantNumeric: "tabular-nums" }}>
              {formatMoneyDisplay(existingInvoice.total || 0)}
            </div>
            {existingInvoice.paid_at && (
              <p className="muted small" style={{ margin: "4px 0 0" }}>
                Paid via <strong>{existingInvoice.paid_method || "offline"}</strong> on{" "}
                {new Date(existingInvoice.paid_at).toLocaleDateString()}.
              </p>
            )}
          </div>
          <InvoiceStatusBadge status={existingInvoice.customer_status} />
        </div>
      )}

      {/* ── Column headers (matches QuoteBuilder) ────────────────── */}
      <div
        className="line-item-row"
        style={{
          marginBottom: "6px",
          fontSize: "0.72rem",
          letterSpacing: "0.04em",
          textTransform: "uppercase",
          color: "var(--text-soft, #6b5448)",
          fontWeight: 600,
        }}
      >
        <span style={{ flex: 1, paddingLeft: "2px" }}>Description</span>
        <span style={{ width: "120px", flexShrink: 0, textAlign: "right" }}>
          {gstRegistered ? "Amount (ex GST)" : "Amount"}
        </span>
        <span style={{ visibility: "hidden", flexShrink: 0 }} aria-hidden>
          Remove
        </span>
      </div>

      {/* ── Line items (same .line-item-row class as QuoteBuilder) ── */}
      <div style={{ display: "flex", flexDirection: "column", gap: "8px", marginBottom: "12px" }}>
        {lineItems.map((item, index) => (
          <div key={item._id} className="line-item-row">
            <input
              type="text"
              placeholder="Description"
              value={item.description}
              onChange={(e) => updateLineItem(index, "description", e.target.value)}
            />
            <input
              type="text"
              inputMode="decimal"
              placeholder="0.00"
              value={formatMoneyInput(item.amount)}
              onChange={(e) => updateLineItem(index, "amount", e.target.value)}
            />
            <Button secondary onClick={() => removeLineItem(index)} disabled={saving}>
              Remove
            </Button>
          </div>
        ))}
      </div>

      <div className="button-row" style={{ marginBottom: "16px" }}>
        <Button secondary onClick={addLineItem} disabled={saving}>
          + Add line item
        </Button>
      </div>

      {/* ── Totals (same .totals-table as QuoteBuilder) ──────────── */}
      <div className="totals-table">
        {gstRegistered ? (
          <>
            <div className="totals-row">
              <span className="totals-label">Subtotal (ex GST)</span>
              <span className="totals-amount">{formatMoneyDisplay(subtotal)}</span>
            </div>
            <div className="totals-row">
              <span className="totals-label">GST (10%)</span>
              <span className="totals-amount">{formatMoneyDisplay(gst)}</span>
            </div>
            <div className="totals-row total">
              <span className="totals-label">Total (inc GST)</span>
              <span className="totals-amount">{formatMoneyDisplay(total)}</span>
            </div>
          </>
        ) : (
          <>
            <div className="totals-row total">
              <span className="totals-label">Total</span>
              <span className="totals-amount">{formatMoneyDisplay(total)}</span>
            </div>
            <p className="muted small" style={{ margin: "10px 0 0" }}>
              GST not applicable — supplier not GST registered.
            </p>
          </>
        )}
      </div>

      {/* ── Notes / amendments ───────────────────────────────────── */}
      <div className="full" style={{ marginTop: "18px" }}>
        <label>Notes / payment terms</label>
        <textarea
          rows={3}
          value={introText}
          onChange={(e) => setIntroText(e.target.value)}
          placeholder="Bank details, payment terms, etc."
        />
      </div>

      <div className="full" style={{ marginTop: "12px" }}>
        <label>Amendments since the quote (optional)</label>
        <textarea
          rows={3}
          value={amendmentsSummary}
          onChange={(e) => setAmendmentsSummary(e.target.value)}
          placeholder="e.g. Added 2 hrs labour for extra wiring requested onsite. Replaced 1 outlet at customer's request."
        />
      </div>

      {/* ── Save & send (matches QuoteBuilder layout) ────────────── */}
      <div className="button-row" style={{ marginTop: "16px" }}>
        <Button onClick={handleSaveAndSend} disabled={saving}>
          <Send size={15} />
          {saving ? "Sending…" : existingInvoice ? "Resend invoice" : "Save & send invoice"}
        </Button>

        {existingInvoice?.public_token && getCustomerInvoiceLink && (
          <Button
            secondary
            disabled={saving}
            onClick={() => {
              const link = getCustomerInvoiceLink(existingInvoice.public_token);
              navigator.clipboard.writeText(link);
              showToast("Customer invoice link copied.", "success");
            }}
          >
            Copy customer invoice link
          </Button>
        )}
      </div>

      {existingInvoice?.public_token && getCustomerInvoiceLink && (
        <div className="customer-link-preview" style={{ marginTop: "10px" }}>
          <label className="muted small" htmlFor="customer-invoice-link-preview">Customer invoice link</label>
          <input
            id="customer-invoice-link-preview"
            type="text"
            readOnly
            value={getCustomerInvoiceLink(existingInvoice.public_token)}
            onFocus={(e) => e.target.select()}
            aria-label="Customer invoice link — read only"
            style={{ width: "100%", marginTop: "6px", fontSize: "0.85rem" }}
          />
        </div>
      )}
      </div>
    </Card>
  );
}
